import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import mammoth from 'mammoth';

interface BookInfo {
  id: string;
  title: string;
  subtitle: string;
  fileName: string;
}

const booksInfo: Record<string, BookInfo> = {
  'adhd-cbt': {
    id: 'adhd-cbt',
    title: 'Mastering Your Mind',
    subtitle: 'A Comprehensive CBT Program for Adults with ADHD',
    fileName: 'Mastering-Your-Mind-CBT-ADHD-Program.docx'
  },
  'autism-cbt': {
    id: 'autism-cbt',
    title: 'The Autistic Navigator',
    subtitle: 'A Comprehensive CBT Program for the Autistic Mind',
    fileName: 'The-Autistic-Navigator-CBT-Program.docx'
  },
  'social-anxiety-cbt': {
    id: 'social-anxiety-cbt',
    title: 'Beyond The Fear',
    subtitle: 'A Comprehensive CBT Program for Social Anxiety',
    fileName: 'Beyond-The-Fear-Social-Anxiety-Program.docx'
  },
  'trauma-cbt': {
    id: 'trauma-cbt',
    title: 'Healing From Trauma',
    subtitle: 'A Comprehensive CBT Program for Trauma Recovery',
    fileName: 'Healing-From-Trauma-CBT-Program.docx'
  }
};

export default function BookReader() {
  const { bookId } = useParams<{ bookId: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chapters, setChapters] = useState<{ title: string; id: string }[]>([]);
  const [currentChapter, setCurrentChapter] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const book = bookId ? booksInfo[bookId] : null;

  const loadBook = useCallback(async () => {
    if (!book) return;

    try {
      setLoading(true);
      const response = await fetch(`/books/${book.fileName}`);
      const arrayBuffer = await response.arrayBuffer();

      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            // Standard heading styles
            "p[style-name='Heading 1'] => h1.chapter-title:fresh",
            "p[style-name='Heading 2'] => h2.section-title:fresh",
            "p[style-name='Heading 3'] => h3.subsection-title:fresh",
            // Alternative heading styles
            "p[style-name='heading 1'] => h1.chapter-title:fresh",
            "p[style-name='heading 2'] => h2.section-title:fresh",
            "p[style-name='heading 3'] => h3.subsection-title:fresh",
            // Title style
            "p[style-name='Title'] => h1.chapter-title:fresh",
            "p[style-name='Subtitle'] => h2.section-title:fresh",
            // Other common variations
            "p[style-name='Heading1'] => h1.chapter-title:fresh",
            "p[style-name='Heading2'] => h2.section-title:fresh",
            "p[style-name='Heading3'] => h3.subsection-title:fresh",
            "p[style-name='List Paragraph'] => li:fresh"
          ]
        }
      );

      let html = result.value;

      // NUCLEAR: Strip ALL style and bgcolor attributes
      html = html.replace(/\s+style\s*=\s*"[^"]*"/gi, '');
      html = html.replace(/\s+style\s*=\s*'[^']*'/gi, '');
      html = html.replace(/\s+bgcolor\s*=\s*"[^"]*"/gi, '');
      html = html.replace(/\s+bgcolor\s*=\s*'[^']*'/gi, '');
      html = html.replace(/\s+background\s*=\s*"[^"]*"/gi, '');
      html = html.replace(/\s+background\s*=\s*'[^']*'/gi, '');

      const chapterList: { title: string; id: string }[] = [];
      let chapterIndex = 0;

      // First, convert standalone bold paragraphs that look like chapters/modules to h1
      // Pattern: <p><strong>Module X: ... or Chapter X: ... or Part X: ...</strong></p>
      html = html.replace(/<p>\s*<strong>\s*((?:Module|Chapter|Part|Section|Week|Session|Introduction|Appendix|Welcome|Conclusion)[^<]*)<\/strong>\s*<\/p>/gi, (match, title) => {
        return `<h1 class="chapter-title">${title.trim()}</h1>`;
      });

      // Also catch numbered modules/chapters like "1. Understanding..." or "Module 1:"
      html = html.replace(/<p>\s*<strong>\s*(\d+\.\s+[^<]+)<\/strong>\s*<\/p>/gi, (match, title) => {
        return `<h1 class="chapter-title">${title.trim()}</h1>`;
      });

      // Process all h1 tags and add IDs
      html = html.replace(/<h1[^>]*>(.*?)<\/h1>/gi, (match, title) => {
        const cleanTitle = title.replace(/<[^>]*>/g, '').trim();
        if (cleanTitle.length > 0 && cleanTitle.length < 200) {
          const id = `chapter-${chapterIndex++}`;
          chapterList.push({ title: cleanTitle, id });
          return `<h1 id="${id}" class="chapter-title">${title}</h1>`;
        }
        return match;
      });

      // Process h2 tags
      html = html.replace(/<h2[^>]*>(.*?)<\/h2>/gi, (match, title) => {
        const id = `section-${chapterIndex++}`;
        return `<h2 id="${id}" class="section-title">${title}</h2>`;
      });

      setChapters(chapterList);
      setContent(html);
      setLoading(false);
    } catch (err) {
      setError('Failed to load book. Please try again.');
      setLoading(false);
    }
  }, [book]);

  useEffect(() => {
    loadBook();
  }, [loadBook]);

  useEffect(() => {
    const handleScroll = () => {
      const chapterElements = document.querySelectorAll('[id^="chapter-"]');
      chapterElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < 200 && rect.top > -rect.height) {
          setCurrentChapter(el.id);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // JAVASCRIPT FIX: Force white background on all elements after content loads
  useEffect(() => {
    if (!loading && content) {
      const forceWhiteBackground = () => {
        const bookContent = document.querySelector('.book-content');
        if (bookContent) {
          // Force white on the container
          (bookContent as HTMLElement).style.setProperty('background', '#FFFFFF', 'important');
          (bookContent as HTMLElement).style.setProperty('background-color', '#FFFFFF', 'important');

          // Force white on ALL children
          const allElements = bookContent.querySelectorAll('*');
          allElements.forEach((el) => {
            const htmlEl = el as HTMLElement;
            // Skip th elements (table headers should be light gray)
            if (el.tagName.toLowerCase() === 'th') {
              htmlEl.style.setProperty('background', '#f5f5f5', 'important');
              htmlEl.style.setProperty('background-color', '#f5f5f5', 'important');
            } else {
              htmlEl.style.setProperty('background', '#FFFFFF', 'important');
              htmlEl.style.setProperty('background-color', '#FFFFFF', 'important');
            }
            htmlEl.style.setProperty('background-image', 'none', 'important');
          });
        }

        // Also force on parent containers
        const mainContent = document.querySelector('.white-content-area');
        if (mainContent) {
          (mainContent as HTMLElement).style.setProperty('background', '#FFFFFF', 'important');
          (mainContent as HTMLElement).style.setProperty('background-color', '#FFFFFF', 'important');
        }
      };

      // Run immediately and after a short delay to catch any late-loading styles
      forceWhiteBackground();
      setTimeout(forceWhiteBackground, 100);
      setTimeout(forceWhiteBackground, 500);
    }
  }, [loading, content]);

  const scrollToChapter = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0D0A0F' }}>
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Book not found</h1>
          <Link to="/library" className="text-amber-200 hover:underline">
            Return to Library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Global style reset for this page */}
      <style>{`
        html, body, #root {
          background: #0D0A0F !important;
        }

        /* NUCLEAR: Override ANY cream/yellow backgrounds */
        .white-content-area,
        .white-content-area *:not(th) {
          background: #FFFFFF !important;
          background-color: #FFFFFF !important;
          background-image: none !important;
        }

        .white-content-area th {
          background: #f0f0f0 !important;
          background-color: #f0f0f0 !important;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive content width */
        .book-reader-content {
          margin: 0 auto;
          padding: 64px 32px;
          max-width: 768px;
        }

        @media (min-width: 1200px) {
          .book-reader-content {
            max-width: 900px;
          }
        }

        @media (min-width: 1400px) {
          .book-reader-content {
            max-width: 1000px;
            padding: 64px 48px;
          }
        }

        @media (min-width: 1600px) {
          .book-reader-content {
            max-width: 1100px;
            padding: 64px 64px;
          }
        }

        @media (min-width: 1800px) {
          .book-reader-content {
            max-width: 1200px;
          }
        }
      `}</style>

      <div className="min-h-screen" style={{ background: '#0D0A0F' }}>
        {/* Header - matching main site style */}
        <header className="fixed top-0 left-0 w-full z-50" style={{ background: 'rgba(13, 10, 15, 0.95)', backdropFilter: 'blur(10px)' }}>
          <nav className="px-8 sm:px-16 py-6">
            <div className="max-w-screen-2xl mx-auto flex justify-between items-center">
              <Link
                to="/"
                className="text-white text-xl font-extralight tracking-widest hover:text-amber-200 transition-colors duration-500"
              >
                BIRGER MOËLL
              </Link>

              <div className="flex items-center space-x-8">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="text-white/60 hover:text-amber-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </button>
                <Link
                  to="/library"
                  className="text-xs uppercase tracking-[0.25em] font-extralight text-white/60 hover:text-amber-200 transition-colors duration-500"
                >
                  Library
                </Link>
              </div>
            </div>
          </nav>
        </header>

        {/* Sidebar */}
        <aside
          className={`fixed top-0 left-0 bottom-0 w-72 z-40 pt-20 transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ background: 'rgba(13, 10, 15, 0.98)', borderRight: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="p-6 h-full overflow-y-auto">
            <h2 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">Contents</h2>
            <nav className="space-y-1">
              {chapters.map((chapter, index) => (
                <button
                  key={chapter.id}
                  onClick={() => scrollToChapter(chapter.id)}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-extralight transition-all ${
                    currentChapter === chapter.id
                      ? 'bg-white/10 text-amber-200'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="text-white/30 mr-2">{index + 1}.</span>
                  {chapter.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content Area - WHITE BACKGROUND */}
        <main
          className={`min-h-screen pt-24 transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-0'}`}
        >
          {/* This is the white content container - NO TAILWIND CLASSES */}
          <div
            className="white-content-area"
            style={{
              minHeight: '100vh',
              background: '#FFFFFF',
              backgroundColor: '#FFFFFF',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
              isolation: 'isolate'
            }}
          >
            <div className="book-reader-content">
              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: '#FFFFFF' }}>
                  <div
                    style={{ width: '48px', height: '48px', border: '4px solid #e5e5e5', borderTopColor: '#333', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' }}
                  />
                  <p style={{ color: '#666' }}>Loading...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '80px 0', background: '#FFFFFF' }}>
                  <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</p>
                  <button
                    onClick={loadBook}
                    style={{ padding: '8px 24px', background: '#111', color: '#fff', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  {/* Book Header */}
                  <header style={{ marginBottom: '48px', paddingBottom: '32px', borderBottom: '1px solid #e5e5e5', background: '#FFFFFF' }}>
                    <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#111', marginBottom: '12px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', background: '#FFFFFF' }}>
                      {book.title}
                    </h1>
                    <p style={{ fontSize: '1.25rem', color: '#666', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', background: '#FFFFFF' }}>
                      {book.subtitle}
                    </p>
                  </header>

                  {/* Book Content */}
                  <article
                    className="book-content"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </>
              )}
            </div>
          </div>
        </main>

        {/* Scoped styles for book content */}
        <style>{`
          .book-content,
          .book-content *,
          .book-content *::before,
          .book-content *::after {
            background: #FFFFFF !important;
            background-color: #FFFFFF !important;
            background-image: none !important;
          }

          .book-content {
            font-family: 'Georgia', 'Times New Roman', serif;
            font-size: 20px;
            line-height: 1.8;
            color: #1a1a1a;
          }

          .book-content h1.chapter-title {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 2em;
            font-weight: 700;
            color: #111;
            margin: 2.5em 0 1em;
            padding-top: 2em;
            border-top: 1px solid #e5e5e5;
          }

          .book-content h1.chapter-title:first-child {
            margin-top: 0;
            padding-top: 0;
            border-top: none;
          }

          .book-content h2.section-title {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 1.5em;
            font-weight: 600;
            color: #111;
            margin: 2em 0 0.75em;
          }

          .book-content h3 {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 1.25em;
            font-weight: 600;
            color: #111;
            margin: 1.5em 0 0.5em;
          }

          .book-content p {
            margin: 1.5em 0;
            color: #292929;
          }

          .book-content ul, .book-content ol {
            margin: 1.5em 0;
            padding-left: 1.5em;
          }

          .book-content li {
            margin: 0.75em 0;
            color: #292929;
          }

          .book-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 2em 0;
            font-size: 0.9em;
          }

          .book-content th {
            background: #f5f5f5 !important;
            background-color: #f5f5f5 !important;
            padding: 1em;
            border: 1px solid #ddd;
            text-align: left;
            font-weight: 600;
            color: #111;
          }

          .book-content td {
            padding: 1em;
            border: 1px solid #ddd;
            color: #292929;
          }

          .book-content blockquote {
            border-left: 3px solid #111;
            margin: 2em 0;
            padding: 0.5em 0 0.5em 2em;
            font-style: italic;
            color: #555;
          }

          .book-content strong {
            font-weight: 700;
            color: #111;
          }

          .book-content a {
            color: #0066cc;
            text-decoration: none;
          }

          .book-content a:hover {
            text-decoration: underline;
          }

          .book-content hr {
            border: none;
            border-top: 1px solid #e5e5e5;
            margin: 3em 0;
          }

          @media (max-width: 1024px) {
            .book-content {
              font-size: 18px;
            }
          }
        `}</style>
      </div>
    </>
  );
}
